import Icon from '@ant-design/icons';
import { MIND_MODE } from '@mindverse/accessor-open';
import { useMemoizedFn } from 'ahooks';
import { message } from 'antd';
import { default as classNames, default as classnames } from 'classnames';
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';

import ChatSvg from '@/components/Icon/svgs/chat.svg';
import LockSvg from '@/components/Icon/svgs/lock.svg';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { EConversationType, markMessagesAsRead } from '@/service/friend';
import type { ICircleConversation, IFriendConversation } from '@/service/friend';
import { HOMEPAGE_FRIEND_STATUS, HOMEPAGE_STATUS } from '@/service/homePage';
import useFriendStore from '@/store/useFriendStore';
import type { ReactClassNameInterface } from '@/types/common';
import { useCircleChatContext } from '@/ui/CircleChat/CircleChatContext';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import Flashback from '@/ui/PAHome/components/LogoBio/Flashback';
import { EVENT } from '@/utils/event';
import reportErrorCustom, { EReportCustomErrorKey } from '@/utils/report/reportErrorCustom';

import ContextBasicInfo from '../../context/BasicInfo';
import { useChatSession } from '../../hooks/useChatSession';
import type { SendMsgData } from '../../hooks/useChatSession';
import useDisableBodyScroll from '../../hooks/useDisableBodyScroll';
import useMindStorage, { EStorageKey } from '../../hooks/useMindStorage';
import type { IConfig, INoLoginConfig } from '../../types/common';
import { EAppMode } from '../../utils/const';
import { CONTAINER_EVENT } from '../../utils/utils';
import ChatList from '../chat';
import Stop from '../stop';
import UserInput from '../userInput';
import styles from './index.less';

const DEFAULT_UPLOAD_TYPE =
  'image/jpeg,image/png,image/gif,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export interface IChatContainerProps extends ReactClassNameInterface {
  appMode: EAppMode;
  disabled?: boolean;
  chatType?: 'normal' | 'spaceCollapse' | 'spaceExpand' | 'homepage' | 'circle'; // space（agent）底下的chat做一下额外的样式适配
  autoCreateSession?: boolean;
  sessionCb?: (mindId: string, sessionId: string) => void;
  inSharedHistory?: boolean;
  config: IConfig;
  noLoginConfig?: INoLoginConfig;
  containerClassName?: string;
  hideQuickNote?: boolean;
  callback?: {
    sendMsgCb?: () => void;
  };
}

const ChatContent = (props: IChatContainerProps): JSX.Element => {
  const {
    appMode,
    autoCreateSession = true,
    chatType = 'normal',
    sessionCb,
    config = {} as IConfig,
    noLoginConfig = {},
    inSharedHistory = false,
    className,
    containerClassName,
    hideQuickNote = false,
    disabled = false,
    callback = {}
  } = props;

  const { t } = useTranslation();

  const { hasInitShortcut, initMessage } = noLoginConfig;
  const { sendMsgCb } = callback;

  const { mindConfig, userId, sessionManager, voiceMute, messageList, originSessionId } = config;
  const mindId = mindConfig.mindId;
  const mindContainerRef = useRef<HTMLDivElement>(null);
  const [isInputFocus, handleSetIsInputFocus] = useState<boolean>(false);
  const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);
  const clearedBadgeSessionRef = useRef<string | null>(null);

  const needLogin = !userId;
  const inChatMode = appMode === EAppMode.CHAT_MODE;
  const inMobile = appMode === EAppMode.CHAT_MOBILE;
  const mode = mindConfig.mode;
  const isPA = mode === MIND_MODE.PERSONAL;

  const [onboard, setOnboard] = useStorage('onboard');

  const _voiceMute = useMemo(() => {
    if (chatType === 'spaceExpand') {
      return false;
    }

    if (chatType === 'spaceCollapse') {
      return true;
    }

    return voiceMute ?? false;
  }, [chatType, voiceMute]);

  // 初始化对话
  const {
    msgList,
    noMore,
    sendMsg,
    doCreateSession,
    receivedAction,
    loadingMsgId,
    fetchHistory,
    isCreatingSession
  } = useChatSession({
    mindConfig,
    sessionManager,
    voiceMute: _voiceMute,
    isLogin: !needLogin,
    messageList: messageList,
    inSharedHistory,
    sendMsgCb,
    disabled
  });

  useDisableBodyScroll(mindContainerRef);

  const [, setFirstSendMsg] = useMindStorage(EStorageKey.FIRST_SEND_MSG);
  const [connectedSessionIdList] = useMindStorage(EStorageKey.CONNECTED_SESSION_ID_LIST);

  const { homepageData } = useHomepageContext();
  const { circleDetail } = useCircleChatContext();

  const conversationList = useFriendStore((state) => state.conversationList);
  const updateFriendList = useFriendStore((state) => state.updateFriendList);
  const updateUnreadList = useFriendStore((state) => state.updateUnreadList);
  const updateFriendCircle = useFriendStore((state) => state.updateFriendCircle);
  const isHomepagePrivate =
    homepageData?.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
    chatType === 'homepage' &&
    homepageData?.status === HOMEPAGE_STATUS.CLOSE;

  // 在组件顶层创建引用
  const pendingMsgRef = useRef<{ sid: string; content: SendMsgData } | null>(null);

  // 创建一个函数来检查并发送消息
  const checkAndSendPendingMessage = useMemoizedFn(() => {
    if (!pendingMsgRef.current) {
      return false;
    }

    const { sid, content } = pendingMsgRef.current;

    // 检查 connectedSessionIdList 是否包含当前的 sid
    if (connectedSessionIdList?.includes(sid)) {
      // 创建session成功后，等待开场白动画后再发送
      setTimeout(() => {
        sendMsg(content, { sessionId: sid })?.then((success) => {
          if (!success) {
            // Send failed, restore input value
            dispatchEvent(
              new CustomEvent(CONTAINER_EVENT.RESTORE_INPUT_VALUE, {
                detail: content.value
              })
            );
          }
        });
        pendingMsgRef.current = null;
      }, 100);

      return true;
    }

    return false;
  });

  // 监听 connectedSessionIdList 变化和强制触发器
  useEffect(() => {
    checkAndSendPendingMessage();
  }, [connectedSessionIdList, checkAndSendPendingMessage]);

  useEffect(() => {
    if (msgList.length === 0) {
      return;
    }

    if (clearedBadgeSessionRef.current === sessionId) {
      return;
    }

    clearedBadgeSessionRef.current = sessionId || '';

    const markAndUpdate = async () => {
      try {
        await markMessagesAsRead({ sessionId });
        // 拿到msgList之后再clear，避免messageStatus全都是READ
        const tempCircle = conversationList?.conversations.find(
          (item): item is ICircleConversation =>
            item.conversationType === EConversationType.CIRCLE &&
            item.circle.route === circleDetail?.route
        );
        const tempUser = conversationList?.conversations.find(
          (item): item is IFriendConversation =>
            item.conversationType === EConversationType.FRIEND &&
            item.friend.route === homepageData?.route
        );

        console.log('123123123 111', tempCircle, tempUser, conversationList, homepageData);

        if (tempCircle) {
          updateFriendCircle({ ...tempCircle, unreadCount: 0, importantMessage: undefined });
        } else if (tempUser) {
          updateFriendList({ isRefresh: true });
          updateUnreadList({ isRefresh: true });
        } else {
          updateFriendList({ isRefresh: true });
          updateUnreadList({ isRefresh: true });
        }
      } catch {
        message.error(t('error.oops'));
      }
    };

    markAndUpdate();
  }, [
    circleDetail?.route,
    conversationList,
    homepageData,
    homepageData?.route,
    msgList,
    sessionId,
    updateFriendCircle,
    updateFriendList,
    updateUnreadList,
    t
  ]);

  const handleSendMsg = useMemoizedFn(
    (
      msg: SendMsgData,
      params?: { sessionId?: string; voiceChannel?: boolean; anonymous?: boolean }
    ): Promise<boolean> | undefined => {
      setFirstSendMsg(onboard ? msg : undefined);

      const sid = params?.sessionId || sessionId;

      console.log('123123123 handleSend', onboard, params?.anonymous, sid);

      if (onboard || (params?.anonymous && !sid && homepageData)) {
        setOnboard(false);

        // 引导页发消息先创建session
        doCreateSession({
          newSession: true,
          extParams:
            params?.anonymous && !sid && homepageData
              ? {
                  sessionType: 'FRIEND',
                  memberUserIds: [homepageData.userId || ''],
                  greetingText: homepageData.greeting || ''
                }
              : undefined
        })
          .then((sessionData) => {
            const { sessionId: _sid } = sessionData;

            // 设置待发送的消息
            pendingMsgRef.current = { sid: _sid, content: msg };

            checkAndSendPendingMessage();
          })
          .catch(() => {
            // Create session failed, restore input value
            dispatchEvent(
              new CustomEvent(CONTAINER_EVENT.RESTORE_INPUT_VALUE, {
                detail: msg.value
              })
            );
          });

        // 由于异步时间太长，先resolve
        return Promise.resolve(true);
      }

      return sendMsg(msg, params);
    }
  );

  useEffect(() => {
    if (mindId && sessionCb) {
      sessionCb(mindId, sessionId ?? '');
    }

    return () => {
      sessionCb?.(mindId, '');
    };
  }, [mindId, sessionId, sessionCb]);

  useEffect(() => {
    const handleImageError = (event: ErrorEvent) => {
      reportErrorCustom(EReportCustomErrorKey.chat_load_error, {
        key1: mindId,
        key2: sessionId,
        key3: event.message
      });
    };

    const container = mindContainerRef.current;

    if (container) {
      container.addEventListener('error', handleImageError, true); // Using capture phase

      return () => {
        container.removeEventListener('error', handleImageError, true);
      };
    }

    return;
  }, [mindId, sessionId]);

  useEffect(() => {
    if (originSessionId) {
      doCreateSession({ originSessionId: originSessionId });

      return;
    }
  }, [originSessionId]);

  useEffect(() => {
    if (!autoCreateSession) {
      return;
    }

    if (originSessionId) {
      return;
    }

    if (onboard === undefined) {
      return;
    }
  }, [onboard, originSessionId, autoCreateSession]);

  const mobileStyle: string[] = [];

  useEffect(() => {
    if (isInputFocus) {
      dispatchEvent(new Event(EVENT.AGENT_SHARE_COLLAPSE));
    }
  }, [isInputFocus]);

  if (inMobile) {
    mobileStyle.push('transition-[margin-right opacity] ease-in-out duration-500');
  }

  return (
    <div className={classnames(styles.mindAppContainer, className)}>
      {/* 主区域 */}
      <div
        ref={mindContainerRef}
        className={classnames(
          styles.mindContainer,
          {
            [styles.inChatMode]: inChatMode
          },
          containerClassName
        )}
        id="mvMindContainer"
      >
        {/* 聊天框 */}
        <div
          className={classNames(
            styles.chatContainer,
            inChatMode ? styles.chatModeContainer : styles.mobileContainer,
            chatType === 'spaceCollapse' ? '!h-0 !flex-grow-0' : ''
          )}
        >
          {!onboard ? (
            <>
              <ChatList
                action={receivedAction}
                fetchHistory={fetchHistory}
                inChatMode={inChatMode}
                inMobile={inMobile}
                inSharedHistory={inSharedHistory}
                initMessage={initMessage}
                initialNoMore={noMore}
                isCreatingSession={isCreatingSession}
                loadingMsgId={loadingMsgId}
                mindId={mindId}
                msgList={msgList}
                needLogin={needLogin}
                onSend={handleSendMsg}
                userId={userId || ''}
              />
              <Stop className={styles.stop} inChatMode={inChatMode} />
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center">
              <div className={styles.linearGradientText}>{t('chatApp.quickMsg.askMe')}</div>
              <Flashback className="mt-4" type="chat" />
            </div>
          )}
        </div>
        {!inSharedHistory && (
          <div
            className={classNames(styles.userInputContainer, 'z-10', {
              [styles.privateInputContainer]: isHomepagePrivate
            })}
          >
            {isHomepagePrivate && (
              <div className={styles.privateNotice}>
                <div className={styles.privateNoticeIconWrap}>
                  <Icon className={styles.privateNoticeIcon} component={LockSvg} />
                </div>
                <div className={styles.privateNoticeText}>
                  <div className={styles.privateNoticeTitle}>{t('homepage.privateAccount')}</div>
                  <div className={styles.privateNoticeDesc}>{t('homepage.addFriendToChat')}</div>
                </div>
              </div>
            )}
            {/* 底部输入框 */}
            <div
              className={classnames(styles.bottomInputWrap, {
                [styles.chatModeInput]: inChatMode,
                [styles.inMobile]: inMobile
              })}
            >
              {isHomepagePrivate ? (
                <div className={styles.privateInput}>
                  <Icon className={styles.privateInputIcon} component={ChatSvg} />
                  <span className={styles.privateInputText}>
                    {t('homepage.chatWith', { name: homepageData.username || '' })}
                  </span>
                  <Icon className={styles.privateInputLock} component={LockSvg} />
                </div>
              ) : (
                <div className={styles.userInputContent}>
                  <UserInput
                    hasInitShortcut={hasInitShortcut}
                    hideQuickNote={hideQuickNote}
                    inChatMode={inChatMode}
                    inMobile={inMobile}
                    initialType="keyboard"
                    isPA={isPA}
                    msgList={msgList}
                    msgLoading={loadingMsgId !== undefined && !homepageData && !circleDetail}
                    needLogin={needLogin}
                    onFocusChanged={handleSetIsInputFocus}
                    onSendMsg={handleSendMsg}
                    sessionManager={sessionManager}
                    uploadType={DEFAULT_UPLOAD_TYPE}
                    userId={userId || ''}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChatContainer = (props: IChatContainerProps) => {
  const { chatType } = useContext(ContextBasicInfo);
  const [onboard, setOnboard] = useStorage('onboard');
  const { inSharedHistory } = props;

  useEffect(() => {
    setOnboard(inSharedHistory ? false : true);
  }, [inSharedHistory]);

  useEffect(() => {
    if (chatType === 'homepage' || chatType === 'circle') {
      setOnboard(false);
    }
  }, [chatType]);

  useEffect(() => {
    function openOnboard() {
      setOnboard(true);
    }
    function closeOnboard() {
      setOnboard(false);
    }

    // 监听展示启动页事件
    addEventListener(CONTAINER_EVENT.ONBOARD, openOnboard);
    addEventListener(CONTAINER_EVENT.CLOSE_ONBOARD, closeOnboard);

    return () => {
      removeEventListener(CONTAINER_EVENT.ONBOARD, openOnboard);
      removeEventListener(CONTAINER_EVENT.CLOSE_ONBOARD, closeOnboard);
    };
  }, []);

  return <ChatContent hideQuickNote={onboard} {...props} />;
};

export default memo(ChatContainer);
